import { useContext, useEffect, useState } from "react"
import Container from "../../../layouts/containers/container";
import PopupContext from "../../../context/context-popup"
import Row from "../../row";
import { ButtonCancel, ButtonProceed, ButtonReturn } from "../../buttons";

import { useMediaQuery } from 'react-responsive';

import CustomDropdown from '../dropdowns/dropdown';;
import CalenderComponent from "./calender";
import Blurred from "../../blurred";
import List from "../../list";

import dayjs from "dayjs";
import moment from 'moment';

import { stringToFriendlyDatetime, stringToDatetimeLocal, getDSTLocalTime } from "../../../lib/helpers/datetime-helpers";
import meetingsApi from "../../../lib/meetings/meetings-api";
import InputsDropdown from "../inputs-dropdown";

const NumberedLabel = ({ number, label }) => {
    return (
        <div className="numbered-label">
            <div className="numbered-label__number">{ number }</div>
            { label }
        </div>
    )
}

const Calender = ({ seamApi, loading, roomId, meetingRoomBookingId, onDaySelection, value, onContinue }) => {
    const isDesktopOrMobile = useMediaQuery({
        query: '(min-width: 1200px)'
    });

    return (
        <div className="date-time-popup__calender">
            <List gap="20px">
                <NumberedLabel number={1} label="Select date for this meeting"/>
                <CalenderComponent seamApi={seamApi} loading={loading} roomId={roomId} meetingRoomBookingId={meetingRoomBookingId} onDaySelection={onDaySelection} value={value}/>
            </List>
            { !isDesktopOrMobile && value !== undefined && <ButtonProceed responsive={false} onClick={onContinue}>Continue</ButtonProceed> }
        </div>
    )
}

const Time = ({
    day,
    value,
    rooms,
    room,
    timeSlots = [],
    startTime,
    setStartTime,
    durationInterval,
    setDurationInterval,
    onDateCreation,
    setPageView,
}) => {
    const timeSlot = timeSlots.find(slot => slot.time === startTime?.value);
    const intervalTimeSelected = durationInterval?.value;
    const selectedRoom = rooms.find(r => r.id === room.meeting_room_id);

    const isDesktopOrMobile = useMediaQuery({
        query: '(min-width: 1200px)'
    });

    const getFormattedTimeSlots = () => {
        return timeSlots?.map((slot) => {
            let obj = {
                label: slot.time,
                value: slot.time,
            }

            if (slot.available === false) obj.disabled = true;

            return obj;
        })
    }

    const getFormattedDurationIntervals = (slot = timeSlot) => {
        return slot?.intervals?.map(interval => {
            let obj = {
                label: `${interval.duration_mins} mins`,
                value: interval,
            }

            if (interval.available === false) obj.disabled = true;

            return obj
        })
    }

    useEffect(() => {
        const [ date, duration ] = value;

        if (date) {
            const newDate = getDSTLocalTime(date).format('HH:mm');

            const time = getFormattedTimeSlots().find((slot) => slot.value === newDate);
            const timeSlot = timeSlots.find(slot => slot.time === time?.value);

            setStartTime(time);

            if (duration !== undefined) {
                const durationValue = getFormattedDurationIntervals(timeSlot)?.find(interval => interval.value?.duration_mins === duration);

                setDurationInterval(durationValue);
            }
        }
    }, [ timeSlots ]);

    return (
        <div className="date-time-popup__time">
            <div className="date-time-popup__times">
                <Blurred toggled={day === null}>
                    <Row gap="40px" justifyContent="space-between" className="date-time-popup-times__time">
                        <NumberedLabel number={2} label="Meeting start time"/>
                        <InputsDropdown
                            items={getFormattedTimeSlots()}
                            onChange={e => {
                                setStartTime(e);
                                setDurationInterval(null);
                            }}
                            value={startTime}
                        />
                    </Row>
                </Blurred>

                <Blurred toggled={startTime === null}>
                    <Row gap="40px" justifyContent="space-between" className="date-time-popup-times__time">
                        <NumberedLabel number={3} label="Meeting duration"/>
                        <InputsDropdown
                            items={getFormattedDurationIntervals()}
                            onChange={e => setDurationInterval(e)}
                            value={durationInterval}
                        />
                    </Row>
                </Blurred>
            </div>

            <div className="date-time-popup__information">
                <List gap="20px">
                    <Container.Text color="#1577EA" fontSize="16px">Summary</Container.Text>
                    <div className="date-time-popup-information__details">
                        <Row gap="10px">
                            <Container.Text color="#354859" fontSize="16px">Room</Container.Text>
                            <Container.Text color="#1577EA" fontSize="16px">{ selectedRoom?.name }</Container.Text>
                        </Row>

                        <Row gap="10px">
                            <Container.Text color="#354859" fontSize="16px">Date</Container.Text>
                            <Container.Text color="#1577EA" fontSize="16px">{ day?.date || '--/--/--' }</Container.Text>
                        </Row>

                        <Row gap="10px">
                            <Container.Text color="#354859" fontSize="16px">Start</Container.Text>
                            <Container.Text color="#1577EA" fontSize="16px">{ (startTime?.value || intervalTimeSelected?.start) || '—:—am' }</Container.Text>
                        </Row>

                        <Row gap="10px">
                            <Container.Text color="#354859" fontSize="16px">End</Container.Text>
                            <Container.Text color="#1577EA" fontSize="16px">{ intervalTimeSelected?.end || '—:—am' }</Container.Text>
                        </Row>
                    </div>
                </List>

                <Row justifyContent='space-between'>
                    { !isDesktopOrMobile && <ButtonCancel mobile onClick={() => setPageView(0)}/> }
                    <Blurred toggled={startTime === null || durationInterval === null}>
                        <ButtonProceed onClick={onDateCreation}>Confirm</ButtonProceed>
                    </Blurred>
                </Row>
            </div>
        </div>
    )
}

export default function DateTimeInputPopup({
    onChange = () => {},
    room,
    rooms,
    value,
    seamApi = false,
}) {
    const { closePopup } = useContext(PopupContext);

    const [ day, setDay ] = useState(null);
    const [ startTime, setStartTime ] = useState(null);
    const [ durationInterval, setDurationInterval ] = useState(null);
    const [ pageView, setPageView ] = useState(0);

    const [ date, duration ] = value;

    const isDesktopOrMobile = useMediaQuery({
        query: '(min-width: 1200px)'
    });

    const onContinue = () => {
        if (date === undefined || duration === undefined) return;

        const day = dayjs(date).startOf('day').format('YYYY-MM-DDTHH:mm');

        let params = {}

        if (room?.id !== undefined && room?.id !== null) {
            params.ignore_meeting_room_booking_ids = [ room.id ]
        }

        if (seamApi) {
            seamApi.getCalendarDayAvailability(room?.meeting_room_id, {
                day,
                ...params
            }).then(({ data }) => {
                onDaySelection({
                    date: dayjs(date).format('YYYY-MM-DD'),
                    ...data
                });
            })
        } else {
            meetingsApi.getCalendarDayAvailability(room?.meeting_room_id, {
                day,
                ...params
            }).then(({ data }) => {
                onDaySelection({
                    date: dayjs(date).format('YYYY-MM-DD'),
                    ...data
                });
            })
        }
    }

    const onDaySelection = (data) => {
        setDay(data);
        setPageView(1);
    }

    const onDateCreation = () => {
        onChange({
            start: day,
            duration: durationInterval?.value,
        });

        closePopup();
    }

    return (
        <div className="date-time-popup__outer">
            <Container className="date-time-popup">
                <Container.Header className="date-time-popup__head">
                    <Row gap="20px" justifyContent="space-between" alignItems="center">
                        <Container.Text color="#1577EA" fontSize="20px">Meeting scheduler for { room?.name }</Container.Text>
                        <ButtonReturn mobile onClick={closePopup}/>
                    </Row>
                </Container.Header>

                <Container.Content className="date-time-popup__content">
                    { isDesktopOrMobile && <>

                        <Calender seamApi={seamApi} roomId={room?.meeting_room_id} meetingRoomBookingId={room?.id} onDaySelection={onDaySelection} value={date}/>
                        <Time
                            rooms={rooms}
                            value={value}
                            room={room}
                            day={day}
                            timeSlots={day?.time_slots}
                            startTime={startTime}
                            setStartTime={setStartTime}
                            durationInterval={durationInterval}
                            setDurationInterval={setDurationInterval}
                            onDateCreation={onDateCreation}
                        />
                    </> }

                    { !isDesktopOrMobile && <>

                        { pageView === 0 && <Calender seamApi={seamApi} onContinue={onContinue} loading={true} roomId={room?.meeting_room_id} meetingRoomBookingId={room?.id} onDaySelection={onDaySelection} value={date}/> }
                        { pageView === 1 && <Time
                            setPageView={setPageView}
                            rooms={rooms}
                            value={value}
                            room={room}
                            day={day}
                            timeSlots={day?.time_slots}
                            startTime={startTime}
                            setStartTime={setStartTime}
                            durationInterval={durationInterval}
                            setDurationInterval={setDurationInterval}
                            onDateCreation={onDateCreation}
                        /> }
                    </>}
                </Container.Content>
            </Container>
        </div>
    )
}
